import { FunctionalComponent, h } from '@stencil/core'
import { Fragment, JSXBase } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import Axios from 'axios'
import { throttle } from 'lodash-es'
import { css } from 'src/lib/emotion'

import { Mark } from '../helpers/mark'
import { InertiaLink } from '../inertia/link/link'
import { classes } from '../utils/css'

export type SearchResult = {
  result_type: 'contact' | 'topic' | 'transcript'
  label_prefix: string
  label: string
  count: number
  url: string
}

export const SearchStore = createStore({
  query: '',
  url: '',
  loading: false,
  results: [] as SearchResult[],
  activeResult: 0,
  transcriptResults: [] as SearchResult[],
  topicResults: [] as SearchResult[],
  callResults: [] as SearchResult[],
})

const { state, onChange } = SearchStore

const search = throttle(async (value) => {
  if (value.length >= 3) {
    const { data: results } = await Axios.get<SearchResult[]>(state.url, { params: { input: value } })
    state.results = results
  } else {
    state.results = []
  }
  state.loading = false
}, 1000)

onChange('query', (value) => {
  state.loading = true
  search(value)
})

onChange('results', (results) => {
  state.activeResult = 0
  if (results) {
    state.topicResults = results.filter((r) => r.result_type === 'topic')
    state.transcriptResults = results.filter((r) => r.result_type === 'transcript')
    state.callResults = results.filter((r) => r.result_type === 'contact').sort((a, b) => b.count - a.count)
  } else {
    state.topicResults = []
    state.transcriptResults = []
    state.callResults = []
  }
})

document.body.addEventListener('click', () => {
  state.query = ''
})

const SearchResultLink: FunctionalComponent<
  { result: SearchResult; index: number } & Parameters<typeof InertiaLink>[0]
> = ({ result, index, ...attrs }) => {
  return (
    <InertiaLink
      {...attrs}
      href={result.url}
      key={result.label}
      onMouseEnter={() => (state.activeResult = index)}
      data-result={index}
      class={`
        flex items-baseline px-2 py-1 -my-1 text-left group relative
      `}
      onClick={() => (state.query = '')}
    >
      <div
        class={`absolute inset-0 bg-white rounded mx-4px my-1px transition duration-200`}
        style={{ opacity: state.activeResult === index ? '.05' : '0' }}
      />
      <span class='relative flex-grow pr-2 subpixel-antialiased font-medium text-surface-highest'>
        {result.label_prefix + ' '}
        <span class='text-glyph-base'>
          <Mark class={`font-bold bg-transparent text-glyph-base`} query={state.query}>
            {result.label}
          </Mark>
        </span>
      </span>
      <span
        class={`
          flex items-center flex-shrink-0 text-xs font-bold self-start mt-2px px-1 justify-center rounded leading-none text-surface-base
        `}
        style={{ background: '#c4c4c4', height: '18px' }}
      >
        {result.count}
      </span>
    </InertiaLink>
  )
}

export const SearchField: FunctionalComponent<JSXBase.IntrinsicElements['span']> = ({
  class: className = '',
  style,
  ...attrs
}) => (
  <span
    onClick={(e) => e.stopImmediatePropagation()}
    style={{
      background: '#2d2c31',
      boxShadow: state.query.length >= 3 ? '0px 5px 20px 0px #00000059' : null,
      fontSize: '14px',
      ...style,
    }}
    class={`
      ${className}
      relative text-glyph-lighter flex items-center h-6 overflow-hidden
      ${classes(state.query.length >= 3, 'rounded-t-md', 'rounded-md')}
    `}
    {...attrs}
  >
    <ui-icon
      icon='search'
      class={`absolute left-0 text-xs ml-8px ${classes(SearchStore.state.query, 'text-glyph-base')}`}
    />
    <input
      type='text'
      class='flex-1 pr-1 pl-6 placeholder-glyph-lighter text-glyph-base truncate bg-transparent'
      placeholder='Search Call Recordings'
      value={state.query}
      onInput={({ target }) => (state.query = (target as HTMLInputElement).value)}
      onKeyUp={(event) => {
        let index = state.activeResult
        const lastIndex = state.results.length - 1
        const target = event.target as HTMLElement
        const callbacks = {
          ArrowUp: () => {
            index = index > 0 ? index - 1 : lastIndex
            state.activeResult = index
          },
          ArrowDown: () => {
            index = index < lastIndex ? index + 1 : 0
            state.activeResult = index
          },
          Enter: () => {
            target.parentElement.querySelector<HTMLAnchorElement>(`[data-result='${index}']`)?.click()
          },
          Escape: () => {
            state.query = ''
          },
        }
        if (event.key in callbacks) {
          callbacks[event.key]()
        }
      }}
    />
    <ui-spinner hidden={!state.loading} class='absolute right-0 mr-8px' />
    <ui-popper margin={0} position='bottom' visible={state.query.length >= 3} match-width>
      <div
        class='flex overflow-hidden flex-col py-2 rounded-b-md'
        style={{ background: '#2d2c31', boxShadow: '0px 5px 20px 0px #00000059' }}
      >
        {!state.loading && !state.results.length ? (
          <span class='px-2 italic font-medium text-surface-higher'>No matches</span>
        ) : state.loading ? (
          <span class='px-2 italic font-medium text-surface-higher'>Searching…</span>
        ) : (
          <Fragment>
            <div class='flex flex-col spacing-y-1 '>
              {!state.transcriptResults.length && (
                <span class='px-2 italic font-medium text-surface-higher'>No transcript matches</span>
              )}
              {state.transcriptResults.map((result, i) => (
                <SearchResultLink result={result} index={i} />
              ))}
            </div>
            <hr class='h-0 border-b' style={{ borderColor: '#96969633', margin: '10px 13px' }} />
            <div class='flex flex-col spacing-y-1 '>
              {!state.topicResults.length && (
                <span class='px-2 italic font-medium text-surface-higher'>No call title matches</span>
              )}
            </div>
            <div class='flex flex-col spacing-y-1 '>
              {!state.callResults.length && !state.topicResults.length && (
                <span class='px-2 italic font-medium text-surface-higher'>No call matches</span>
              )}
              {state.topicResults.map((result, i) => (
                <SearchResultLink result={result} index={i + state.transcriptResults.length} />
              ))}
              {state.callResults.map((result, i) => (
                <SearchResultLink
                  result={result}
                  index={i + state.topicResults.length + state.transcriptResults.length}
                />
              ))}
            </div>
          </Fragment>
        )}
      </div>
    </ui-popper>
  </span>
)

const styleSearchBoarder = css`
  background: #26252a;
  &:hover,
  &:focus-within {
    background: linear-gradient(to right, #ff4f4f, #deac4d, #bc4fff, #4f6bff);
  }
`

export const AiSearchField: FunctionalComponent<
  JSXBase.IntrinsicElements['div'] & {
    aiSearchCreateQueryUrl: string
  }
> = ({ class: className = '', style, aiSearchCreateQueryUrl, ...attrs }) => (
  <div
    style={style}
    class={`
      ${className} ${styleSearchBoarder}
      relative flex items-center h-6 font-medium p-1px rounded-3xl
    `}
    {...attrs}
  >
    <span
      onClick={(e) => e.stopImmediatePropagation()}
      class={`flex items-center pr-7 pl-1 w-full h-full gap-x-2 rounded-3xl `}
      style={{
        background: '#26252A',
        boxShadow: state.query.length >= 3 ? '0px 5px 20px 0px #00000059' : null,
        fontSize: '14px',
        color: '#818181',
        ...style,
      }}
    >
      <ui-icon icon='ai-search' class='ml-8px text-lg' />
      <input
        type='text'
        class='flex-1 placeholder-glyph-lighter text-glyph-base truncate bg-transparent'
        placeholder='Ask Fathom AI or search…'
        value={state.query}
        onInput={({ target }) => (state.query = (target as HTMLInputElement).value)}
        onKeyUp={(event) => {
          let index = state.activeResult
          const lastIndex = state.results.length - 1
          const target = event.target as HTMLElement
          const callbacks = {
            ArrowUp: () => {
              index = index > 0 ? index - 1 : lastIndex
              state.activeResult = index
            },
            ArrowDown: () => {
              index = index < lastIndex ? index + 1 : 0
              state.activeResult = index
            },
            Enter: () => {
              target.parentElement.querySelector<HTMLAnchorElement>(`[data-result='${index}']`)?.click()
            },
            Escape: () => {
              state.query = ''
            },
          }
          if (event.key in callbacks) {
            callbacks[event.key]()
          }
        }}
      />
      <ui-spinner hidden={!state.loading} class='absolute right-0 mr-3' />
      <ui-popper margin={5} position='bottom' visible={state.query.length >= 3} match-width>
        <div
          class='flex overflow-hidden flex-col py-2 rounded-md border border-gray-700 border-solid'
          style={{ background: '#26252A', boxShadow: '0px 5px 20px 0px #00000059', minWidth: '380px' }}
        >
          <ai-search-submit-new-search-button
            class='mx-2'
            query={state.query}
            createQueryUrl={aiSearchCreateQueryUrl}
            onSearchSubmitted={() => {
              state.query = ''
            }}
          />
          <div class='my-1 mx-2 h-2px bg-gray-700' />
          {!state.loading && !state.results.length ? (
            <span class='px-2 italic font-medium text-surface-higher'>No matches</span>
          ) : state.loading ? (
            <span class='px-2 italic font-medium text-surface-higher'>Searching…</span>
          ) : (
            <Fragment>
              <div class='flex flex-col spacing-y-1 '>
                {!state.transcriptResults.length && (
                  <span class='px-2 italic font-medium text-surface-higher'>No transcript matches</span>
                )}
                {state.transcriptResults.map((result, i) => (
                  <SearchResultLink result={result} index={i} />
                ))}
              </div>
              <hr class='h-0 border-b' style={{ borderColor: '#96969633', margin: '10px 13px' }} />
              <div class='flex flex-col spacing-y-1 '>
                {!state.topicResults.length && (
                  <span class='px-2 italic font-medium text-surface-higher'>No call title matches</span>
                )}
              </div>
              <div class='flex flex-col spacing-y-1 '>
                {!state.callResults.length && !state.topicResults.length && (
                  <span class='px-2 italic font-medium text-surface-higher'>No call matches</span>
                )}
                {state.topicResults.map((result, i) => (
                  <SearchResultLink result={result} index={i + state.transcriptResults.length} />
                ))}
                {state.callResults.map((result, i) => (
                  <SearchResultLink
                    result={result}
                    index={i + state.topicResults.length + state.transcriptResults.length}
                  />
                ))}
              </div>
            </Fragment>
          )}
        </div>
      </ui-popper>
    </span>
  </div>
)
